import { h, markRaw } from 'vue';
import { fireTag } from '@vue-util/tagManager';
import { Cookie } from '@vue-util/cookie';
import Experimentation from '@common-vue/util/src/modules/experimentation/Experimentation';
import { CS_PHONE_NUMERIC } from 'config';
import setCookiesFromResponse from '#utils/setCookiesFromResponse';
import { SOURCE_ACTION_MAP } from '#src/constants';
import experiments from '../configs/Experiments';

const BrandName = (process.env.ENTRY_POINT === 'mcom') ? 'Macys' : 'Bloomingdales';

const technicalGlitchError = `Please try again, a technical issue occurred. If you continue to experience difficulties you can order by phone at ${CS_PHONE_NUMERIC}.`;
const preRenderBag = (killswitches) => {
  try {
    if (killswitches?.isPreRenderBagEnabled) {
      const iframe = document.createElement('iframe');
      iframe.style = 'height:0; border:0';
      iframe.src = '/my/bag?prerender=true';
      document.body.appendChild(iframe);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Unable to prerender the bag', err);
  }
};

const updateKSBasedOnExperiment = async (KS = {}) => {
  const isPriceSimplificationEnabledInBE = KS.isPriceSimplificationEnabled;
  const promises = Object.entries(experiments).map(async ([experimentId, killswitchName]) => {
    const killswitchValue = KS[killswitchName];
    // ( killswitchValue === undefined ) - means KS doesn't exists in BE code. It's a custom KS on UI side.
    if (killswitchValue || killswitchValue === undefined) {
      KS[killswitchName] = await Experimentation.isRecipeSelected(experimentId);
    }
  });
  await Promise.allSettled(promises);
  // isPriceSimplificationVarient2Enabled and isPriceSimplificationVarient3Enabled both are custom UI killswitches
  if (isPriceSimplificationEnabledInBE && (KS.isPriceSimplificationVarient2Enabled || KS.isPriceSimplificationVarient3Enabled
    || KS.isPriceSimplificationVarient4Enabled || KS.isPriceSimplificationVarient5Enabled
    || KS.isPriceSimplificationVarient6Enabled || KS.isPriceSimplificationVarient7Enabled
    || KS.isPriceSimplificationVarient34Enabled || KS.isPriceSimplificationVarient36Enabled)) {
    KS.isPriceSimplificationEnabled = true;
  }
  return KS;
};

const getPageViewTagData = ({
  bagData = {}, device, options,
}) => {
  const getArrayValue = (val) => (Array.isArray(val) ? val : ([val || '']));
  const analyticsData = bagData.meta?.analytics?.data || {};
  const itemFlags = bagData.sections?.bagItems?.items?.[0]?.flags || {};
  const bagAttributes = bagData.meta?.bagAttributes || {};
  const shippingThreshold = bagData.sections?.shippingThreshold || {};

  const isAvblForShip = `ship-${itemFlags.shippingAvailability ? 'y' : 'n'}`;
  const isAbvlForBOPS = `bops-${itemFlags.storeAvailability ? 'y' : 'n'}`;
  const isAvblForBOSS = `boss-${itemFlags.shipToStoreAvailability ? 'y' : 'n'}`;
  const itemAvailibilityArray = [isAvblForShip, isAbvlForBOPS, isAvblForBOSS];
  const itemAvailibility = itemAvailibilityArray.join(':');

  const availableFulFillments = itemAvailibilityArray.reduce((accumulator, currentItem) => {
    if (currentItem.includes('-y')) {
      accumulator.push(currentItem.replace('-y', ''));
    }
    return accumulator;
  }, []);

  let itemAvailabilitySelect = '';
  if (itemFlags.pickUpFromStore) {
    itemAvailabilitySelect = 'bops';
  } else if (itemFlags.shipToStoreSelected) {
    itemAvailabilitySelect = 'boss';
  } else if (itemFlags.shippingAvailability) {
    itemAvailabilitySelect = 'ship';
  }

  const { source, sourceUtagData, allCookies } = options;

  let linkName = 'add to bag';
  if (sourceUtagData?.buyNow) {
    linkName = process.env.ENTRY_POINT === 'mcom' ? 'buy now' : 'atb buy now click';
  }

  const {
    shippingCountry = 'US',
  } = allCookies;
  const uTagData = {
    link_name: linkName,
    // adobe_page_name: `${process.env.ENTRY_POINT}:add to bag:add to bag`,
    consolidated_product: getArrayValue(analyticsData.consolidated_product),
    CustomerFirstName: Cookie.getMultiValueCookie('UserName', 'MISCGCs', allCookies) || '',
    // page_type: (source === 'REGA2B') ? 'registry' : 'add to bag',
    product_add_type: (source === 'REGA2B') ? 'registry' : 'product',
    // page_name: 'add to bag',
    event_name: process.env.ENTRY_POINT === 'mcom' ? 'add to bag' : 'add_to_bag',
    country: shippingCountry,
    // device logic here is similar to one in @component/common/src/util/RequestContext.js
    // First check for existence of device from request headers , X-${BrandName}-Device` else check DT from MISCGC's
    device: (device || Cookie.getMultiValueCookie('DT', 'MISCGCs', allCookies) || '').toUpperCase(),
    is_big_ticket: getArrayValue(analyticsData.is_big_ticket),
    postalCode: Cookie.getMultiValueCookie('BTZIPCODE', 'MISCGCs', allCookies),
    product_availability: getArrayValue(itemAvailibility),
    product_availability_select: getArrayValue(itemAvailabilitySelect),
    product_brand: getArrayValue(bagData?.sections?.bagItems?.items?.[0]?.product?.detail?.brand),
    product_color: getArrayValue(analyticsData.product_color),
    product_fulfillment_method: getArrayValue(analyticsData.product_fulfillment_method),
    product_fulfillment_option: getArrayValue(availableFulFillments.join('|')),
    product_id: getArrayValue(analyticsData.product_id),
    product_name: getArrayValue(analyticsData.product_name),
    product_original_price: getArrayValue(analyticsData.product_original_price),
    product_price: getArrayValue(analyticsData.product_price),
    product_pricing_state: getArrayValue(analyticsData.product_pricing_state),
    product_protection_plan: getArrayValue(analyticsData.product_protection_plan),
    product_quantity: getArrayValue(analyticsData.product_quantity),
    product_size: getArrayValue(analyticsData.product_size),
    product_sub_total: getArrayValue(analyticsData.product_sub_total),
    product_upc: getArrayValue(analyticsData.product_upc),
    site_type: bagData.meta?.context?.deviceType || 'responsive',
    subscription_product: analyticsData.subscription_product || false,
    shipping_fee: getArrayValue(bagData.sections?.summary?.price?.[1]?.values?.[0]?.formattedValue),
    shipping_threshold: getArrayValue(shippingThreshold.data?.freeShippingThresholdAmount),
    shipping_value: getArrayValue(bagData.sections?.summary?.price?.[1]?.values?.[0]?.formattedValue),
    t_category_id: getArrayValue(analyticsData.product_category_id),
    t_category_name: getArrayValue(analyticsData.product_category_name),
    product_category_id: getArrayValue(analyticsData.product_category_id),
    product_category_name: getArrayValue(analyticsData.product_category_name),
  };

  // #region free_shipping_threshold_met tagging data
  const { srfreeShipping, data: shippingThresholdData, everyItemBOPSSelected } = shippingThreshold;
  let freeShippingThresholdMet = analyticsData?.free_shipping_threshold_met;
  /**
   * If BE is not sending free_shipping_threshold_met in analytics data, then determine same in UI
   * Refer my-bag-consumable/src/components/FreeShipping/FreeShippingMCOM.vue to understand why below conditions are needed
   */
  if (!everyItemBOPSSelected) {
    if (freeShippingThresholdMet === undefined && (
      bagAttributes.redCarpetUser
      || srfreeShipping
      || (shippingThresholdData && !shippingThresholdData?.freeShippingThresholdReminderAmount)
    )) {
      freeShippingThresholdMet = 'true';
    } else if (shippingThresholdData?.freeShippingThresholdReminderAmount) {
      freeShippingThresholdMet = 'false';
    }
  }
  if (freeShippingThresholdMet !== undefined) {
    // free_shipping_threshold_met - tagging property - String
    uTagData.free_shipping_threshold_met = freeShippingThresholdMet;
  }
  // #endregion free_shipping_threshold_met

  return {
    ...uTagData,
    ...(sourceUtagData || {}),
  }; // product_type should come from PDP as per backbone (['member'] or ['single item']) };
};

const addOrUpdateItemToBag = (payload, options = {}) => new Promise((resolve, reject) => {
  const {
    useModal, registrants, isUpdate, sequenceNumber, bagGuid,
  } = options;

  const rejectReason = (name, message, data = {}) => {
    fireTag('link', {
      event_name: 'add to bag error',
      link_name: 'add to bag error',
      error_category: (payload?.source === 'REGA2B') ? 'registry' : 'product',
      error_code: [data.errorCode || ''],
      error_label: [name],
      error_message: [data.bagSpecificMessage || technicalGlitchError],
      error_misc: [(data.productId || '').toString()],
      error_scope: ['global'],
    });
    return new Error(data.bagSpecificMessage || technicalGlitchError, { cause: { name, message } });
  };
  const add = async () => {
    try {
      const importATBConfirmation = import(/* webpackChunkName: 'ATBConfirmation' */ '#components/ATBConfirmation');
      const response = await fetch(isUpdate ? `/xapi/bag/v1/${bagGuid}/items/${sequenceNumber}` : '/xapi/bag/v1/add', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-requested-with': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const json = await response.json();
        const item = json?.bag?.sections?.bagItems?.items?.[0];
        const error = json?.bag?.meta?.messages?.error || item?.messages?.error;
        if (error?.[0]) {
          const data = {
            errorCode: item?.errorCodes?.[0],
            bagSpecificMessage: error[0],
            productId: item?.product?.id,
          };
          reject(rejectReason('API_RESPONSE_ERROR', error[0], data));
        } else {
          const { headers } = response;
          // setting  KillSwitches based on experiment checks (i.e. true if under treatment, false if under control or holdout)
          const KillSwitches = await updateKSBasedOnExperiment({ ...json?.bag?.meta?.killswitches });
          preRenderBag(KillSwitches);
          const ATBConfirmation = (await importATBConfirmation).default;
          const device = (headers?.get(`X-${BrandName}-Device`) || '').toUpperCase();
          options.source = payload.item?.source;
          const isAllRequiredCookiesSet = setCookiesFromResponse(json);

          if (isAllRequiredCookiesSet) {
            const allCookies = Cookie.parse() || {};
            const userState = Cookie.getUserStatusFromCookie(allCookies);
            options.allCookies = allCookies;
            const pageViewTagData = getPageViewTagData({
              bagData: json?.bag, device, options,
            });
            // TODO: actionTagData currently used only when continue shopping is clicked
            const actionTagData = {
              CustomerFirstName: pageViewTagData.CustomerFirstName,
              country: pageViewTagData.country,
              postalCode: pageViewTagData.postalCode,
              device: pageViewTagData.device,
            };

            /**
             * As part of latest requirement DBAG-4790 / PDPR-2198 we are making below changes
             * 1. Converted 'view' to 'link' tag
             * 2. Not passing adobe_page_name, page_name, page_type to utag data , so commented above
             * 3. event_name will be 'add to bag' for mcom and 'add_to_bag' for 'bcom'
             * 4. For add to bag case, link_name will be 'add to bag'
             * 5. For Buy Now case, link_name will be 'buy now' for 'mcom' and 'atb buy now click' for 'bcom'
             * 6. updated related test cases in useAddToBag.test.js
             */
            fireTag('link', pageViewTagData);
            resolve({
              data: json?.bag,
              component: markRaw(h(ATBConfirmation,
                {
                  onFireContinueShoppingLinkTag: (utagData) => { fireTag('link', { ...utagData, ...actionTagData }); },
                  useModal,
                  registrants,
                  isUpdate,
                  bag: json?.bag,
                  ks: KillSwitches,
                  signedIn: userState.isSignedIn(),
                  source: SOURCE_ACTION_MAP[options.source] || options.source, // When payload's item.source is PDPUB ,
                  // we send item.source as PDPUB to backend XAPI Call
                  // but to ATBConfirmation component we send PDPA2B as source to show PDP's version for ATBConfirmation to user
                })),
            });
          }
        }
      } else if (response.status === 428 && window.ak_chlge && typeof window.ak_chlge.showChallenge === 'function') {
        const responseBody = await response.json();
        window.ak_chlge.showChallenge(responseBody, window.location.href);
      } else {
        reject(rejectReason('API_ERROR', `ATB call failed with ${response.status} (${response.statusText})`));
      }
    } catch (error) {
      reject(rejectReason('UNKNOWN_ERROR', typeof error === 'string' ? error : `${error.name}: ${error.message}`));
    }
  };
  add();
});

export default addOrUpdateItemToBag;
